import { Link } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocalePath } from "../hooks/useLocalePath";
import { SEOHead } from "../components/SEOHead";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import {
  ChevronRight, ChevronDown, ShieldCheck, HelpCircle, Compass,
  CheckCircle2, FileText, ClipboardList, Search, Send, FolderOpen,
  Landmark, Clock, BarChart3, ArrowRight,
} from "lucide-react";
import { SiteFooter } from "../components/SiteFooter";
import { DVGuidesNav } from "../components/DVGuidesNav";
import { useAnalytics, EVENTS } from "../hooks/useAnalytics";

const stepIcons = [Search, Send, FolderOpen, Landmark, BarChart3];
const stepColors = [
  "bg-teal-700 text-white",
  "bg-sky-600 text-white",
  "bg-violet-600 text-white",
  "bg-amber-600 text-white",
  "bg-emerald-600 text-white",
];

export default function DVAfterWinningPage() {
  const { t } = useTranslation();
  const lp = useLocalePath();
  const { trackEvent } = useAnalytics();
  const [openFaq, setOpenFaq] = useState(null);

  const docs = t("dvw.docs", { returnObjects: true });
  const faqs = t("dvw.faqs", { returnObjects: true });

  return (
    <div className="min-h-screen bg-background" data-testid="dv-after-winning-page">
      <SEOHead
        title={t("dvw.heroTitle")}
        description={t("dvw.heroSubtitle")}
      />

      {/* HERO */}
      <section className="hero-gradient py-14 md:py-24" data-testid="dvw-hero">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <Badge variant="secondary" className="bg-teal-100 text-teal-800 hover:bg-teal-100 mb-5 font-medium">
            <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
            {t("dvw.badge")}
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-slate-900 mb-5">
            {t("dvw.heroTitle")}
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-slate-600 mb-8 max-w-2xl mx-auto">
            {t("dvw.heroSubtitle")}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={lp("/auth?mode=register&redirect=%2Fchecklists")} data-testid="dvw-hero-checklist">
              <Button size="lg" className="bg-teal-700 hover:bg-teal-800 text-white font-medium shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 w-full sm:w-auto px-8 py-6 text-base"
                onClick={() => trackEvent(EVENTS.GENERATE_CHECKLIST, { source: "after_winning_hero" })}>
                <ClipboardList className="mr-2 h-4 w-4" /> {t("dvw.ctaChecklist")}
              </Button>
            </Link>
            <Link to={lp("/gc/green-card-timeline")} data-testid="dvw-hero-timeline">
              <Button size="lg" variant="outline" className="border-slate-200 hover:bg-slate-50 font-medium w-full sm:w-auto px-8 py-6 text-base"
                onClick={() => trackEvent(EVENTS.TIMELINE_CALCULATOR, { source: "after_winning_hero" })}>
                <Clock className="mr-2 h-4 w-4" /> {t("dvw.ctaTimeline")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 1 — CONFIRM YOUR SELECTION */}
      <section className="py-14 md:py-20 bg-white" data-testid="dvw-confirm">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvw.confirmLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvw.confirmTitle")}</h2>
          </div>
          <div className="max-w-3xl mx-auto space-y-5">
            <Card className="border-slate-100 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center flex-shrink-0">
                    <Search className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-slate-900 mb-1.5">{t("dvw.confirmCheckTitle")}</h3>
                    <p className="text-sm leading-relaxed text-slate-600">{t("dvw.confirmCheckDesc")}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-amber-200/60 bg-amber-50/30 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-slate-900 mb-1.5">{t("dvw.confirmScamTitle")}</h3>
                    <p className="text-sm leading-relaxed text-slate-600">{t("dvw.confirmScamDesc")}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* SECTION 2 — SUBMIT FORM DS-260 */}
      <section className="py-14 md:py-20 bg-slate-50/60" data-testid="dvw-ds260">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvw.ds260Label")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvw.ds260Title")}</h2>
            <p className="mt-3 text-base text-slate-600 max-w-2xl mx-auto">{t("dvw.ds260Intro")}</p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Card className="border-slate-100 shadow-sm">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-xl bg-sky-50 text-sky-700 flex items-center justify-center flex-shrink-0">
                    <Send className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-slate-900 mb-1.5">{t("dvw.ds260WhatTitle")}</h3>
                    <p className="text-sm leading-relaxed text-slate-600">{t("dvw.ds260WhatDesc")}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-xl bg-violet-50 text-violet-700 flex items-center justify-center flex-shrink-0">
                    <FileText className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-slate-900 mb-1.5">{t("dvw.ds260InfoTitle")}</h3>
                    <p className="text-sm leading-relaxed text-slate-600">{t("dvw.ds260InfoDesc")}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* SECTION 3 — PREPARE DOCUMENTS */}
      <section className="py-14 md:py-20 bg-white" data-testid="dvw-documents">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvw.docsLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvw.docsTitle")}</h2>
            <p className="mt-3 text-base text-slate-600 max-w-2xl mx-auto">{t("dvw.docsIntro")}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {Array.isArray(docs) && docs.map((doc, i) => (
              <Card key={i} className="border-slate-100 shadow-sm hover:shadow-md transition-all duration-300" data-testid={`dvw-doc-${i}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900 mb-1">{doc.title}</h3>
                      <p className="text-sm text-slate-600">{doc.desc}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to={lp("/auth?mode=register&redirect=%2Fchecklists")} data-testid="dvw-docs-cta">
              <Button className="bg-teal-700 hover:bg-teal-800 text-white font-medium">
                <ClipboardList className="mr-2 h-4 w-4" /> {t("dvw.ctaChecklist")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 4 — INTERVIEW PREPARATION */}
      <section className="py-14 md:py-20 bg-slate-50/60" data-testid="dvw-interview">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvw.interviewLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvw.interviewTitle")}</h2>
            <p className="mt-3 text-base text-slate-600 max-w-2xl mx-auto">{t("dvw.interviewIntro")}</p>
          </div>
          <div className="max-w-3xl mx-auto space-y-5">
            <Card className="border-slate-100 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center flex-shrink-0">
                    <Landmark className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-slate-900 mb-1.5">{t("dvw.interviewSchedTitle")}</h3>
                    <p className="text-sm leading-relaxed text-slate-600">{t("dvw.interviewSchedDesc")}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-100 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center flex-shrink-0">
                    <FileText className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-slate-900 mb-1.5">{t("dvw.interviewPrepTitle")}</h3>
                    <p className="text-sm leading-relaxed text-slate-600">{t("dvw.interviewPrepDesc")}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* SECTION 5 — VISA BULLETIN AND CASE NUMBERS */}
      <section className="py-14 md:py-20 bg-white" data-testid="dvw-visa-bulletin">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvw.bulletinLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvw.bulletinTitle")}</h2>
            <p className="mt-3 text-base text-slate-600 max-w-2xl mx-auto">{t("dvw.bulletinIntro")}</p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Card className="border-slate-100 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-slate-900 mb-1.5">{t("dvw.bulletinCaseTitle")}</h3>
                    <p className="text-sm leading-relaxed text-slate-600">{t("dvw.bulletinCaseDesc")}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="mt-8 text-center">
            <Link to={lp("/gc/green-card-timeline")} data-testid="dvw-bulletin-cta">
              <Button variant="outline" className="font-medium border-slate-200">
                <Clock className="mr-2 h-4 w-4 text-teal-600" /> {t("dvw.ctaTimeline")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <DVGuidesNav currentPath="/what-to-do-after-winning-dv-lottery" />

      {/* SECTION 6 — FAQ */}
      <section className="py-14 md:py-20 bg-slate-50/60" data-testid="dvw-faq">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvw.faqLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvw.faqTitle")}</h2>
          </div>
          <div className="space-y-3">
            {Array.isArray(faqs) && faqs.map((faq, i) => {
              const isOpen = openFaq === i;
              return (
                <Card key={i} className="border-slate-100 shadow-sm overflow-hidden" data-testid={`dvw-faq-${i}`}>
                  <button
                    className="w-full flex items-start gap-3 p-5 text-left hover:bg-slate-50/50 transition-colors"
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    data-testid={`dvw-faq-toggle-${i}`}
                  >
                    <HelpCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm font-semibold text-slate-900 flex-1">{faq.q}</span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 flex-shrink-0 mt-0.5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5">
                      <p className="text-sm leading-relaxed text-slate-600 ml-8">{faq.a}</p>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-14 md:py-24 bg-teal-700" data-testid="dvw-final-cta">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <Compass className="h-10 w-10 text-teal-200 mx-auto mb-5" />
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-white mb-3">{t("dvw.finalTitle")}</h2>
          <p className="text-base text-teal-100 mb-8 max-w-xl mx-auto">{t("dvw.finalSubtitle")}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={lp("/auth?mode=register")} data-testid="dvw-final-start">
              <Button size="lg" className="bg-white text-teal-700 hover:bg-teal-50 font-medium shadow-lg w-full sm:w-auto px-6 py-6 text-base">
                <Compass className="mr-2 h-4 w-4" /> {t("dvw.ctaStartGuide")}
              </Button>
            </Link>
            <Link to={lp("/auth?mode=register&redirect=%2Fchecklists")} data-testid="dvw-final-checklist">
              <Button size="lg" variant="outline" className="border-teal-400 text-white hover:bg-teal-600 font-medium w-full sm:w-auto px-6 py-6 text-base">
                <ClipboardList className="mr-2 h-4 w-4" /> {t("dvw.ctaChecklist")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
